import React, { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Dropdown, IDropdownItem } from 'components/UI/Dropdown/Dropdown'
import { PiToolbox, PiCalendarStar, PiShippingContainer } from 'react-icons/pi'
import { BsTruck } from 'react-icons/bs'
import {
  Handshake,
  NotebookPen,
  UserPlus,
  Tags,
  Edit,
  Combine,
} from 'lucide-react'
import CreateDealModal from 'components/Deals/CreateDealModal/CreateDealModal'
import NotesModal from 'components/Modals/NotesModal'
import NewContactModal from 'components/Modals/NewContactModal'
import SuggestionModal from 'components/Modals/SuggestionModal/SuggestionModal'
import { SingleChainMergeModal } from 'features/unmatched-companies/SingleChainMergeModal'
import ReminderModal from 'components/Reminders/ReminderModal'
import { featureFlagService } from 'utils/featureFlagService'
import {
  SingleItemTagAssignModal,
  SingleItemDistributorAssignModal,
  SingleItemGPOAssignModal,
} from './SingleItemAssignmentModals'
import {
  TableTypeSlug,
  ContactCompany,
  CompanyTypeSlug,
} from 'models/companies'

interface CompanyActionsProps {
  contactCompanyId?: number // Make optional for door pages
  doorId?: number // Actual door/restaurant ID
  doorProxyId?: number // Door proxy ID for API operations
  tableTypeSlug: TableTypeSlug
  contactCompany?: ContactCompany
  companyName?: string
  websiteAddress?: string
  address?: {
    addressInfo?: {
      fullAddress?: string
    }
  }
  phone?: string
  className?: string
  companyTypeSlug?: CompanyTypeSlug
  isUserCreated?: boolean // Whether this is a user-created (unmatched) company
}

export function CompanyActionsComponent({
  contactCompanyId,
  doorId,
  doorProxyId,
  tableTypeSlug,
  contactCompany,
  companyName,
  websiteAddress,
  address,
  phone,
  className,
  companyTypeSlug,
  isUserCreated = false,
}: CompanyActionsProps) {
  const queryClient = useQueryClient()
  const featureFlag = featureFlagService()

  // Create a fallback contactCompany object if one isn't provided
  // For company pages, the contactCompany represents the company itself
  // For door pages, the contactCompany represents the parent company, not the door
  const effectiveContactCompany = contactCompany || {
    id: contactCompanyId!, // Always use the company ID, never the door ID
    name: companyName || '',
    domain: websiteAddress || '',
    // Add minimal required fields for the contact modal
  }

  // Use doorProxyId as primary identifier for door API operations, otherwise contactCompanyId
  const primaryId = doorProxyId || contactCompanyId

  // Modal states
  const [showCreateDeal, setShowCreateDeal] = useState(false)
  const [showCreateNote, setShowCreateNote] = useState(false)
  const [showAddContact, setShowAddContact] = useState(false)
  const [showManageTags, setShowManageTags] = useState(false)
  const [showAddDistributor, setShowAddDistributor] = useState(false)
  const [showAddGPO, setShowAddGPO] = useState(false)
  const [showSuggestEdit, setShowSuggestEdit] = useState(false)
  const [showMergeCompany, setShowMergeCompany] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)

  const dropdownItems: IDropdownItem[] = [
    {
      label: 'Create a Deal',
      icon: <Handshake size={16} />,
      callback: () => setShowCreateDeal(true),
    },
    {
      label: 'Create a Note',
      icon: <NotebookPen size={16} />,
      callback: () => setShowCreateNote(true),
    },
    {
      label: 'Add a Contact',
      icon: <UserPlus size={16} />,
      callback: () => setShowAddContact(true),
    },
    {
      label: 'Manage Tags',
      icon: <Tags size={16} />,
      callback: () => setShowManageTags(true),
    },
    {
      label: 'Add Distributor',
      icon: <BsTruck size={16} />,
      callback: () => setShowAddDistributor(true),
    },
    {
      label: 'Add GPO',
      icon: <PiShippingContainer size={16} />,
      callback: () => setShowAddGPO(true),
    },
    ...(featureFlag.enableReminders
      ? [
          {
            label: 'Schedule Reminder',
            icon: <PiCalendarStar size={16} />,
            callback: () => setShowReminderModal(true),
          },
        ]
      : []),
    // Conditionally add actions based on whether it's user-created
    ...(isUserCreated
      ? // For user-created companies (not doors), add merge action
        !doorProxyId
        ? [
            {
              label: 'Merge Company',
              icon: <Combine size={16} />,
              callback: () => setShowMergeCompany(true),
            },
          ]
        : [] // For user-created doors, no additional actions
      : [
          // For regular companies/doors, add suggest edit
          {
            label: 'Suggest an Edit',
            icon: <Edit size={16} />,
            callback: () => setShowSuggestEdit(true),
          },
        ]),
  ]

  const handleRefreshData = () => {
    // Invalidate all company-related queries to ensure CompanyAttributesManager refreshes
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey
        if (!Array.isArray(key)) return false

        // Invalidate any query that might contain this company's data
        const shouldInvalidate = key.some((k) => {
          if (typeof k === 'number') {
            return (
              k === primaryId || k === contactCompanyId || k === doorProxyId
            )
          }
          if (typeof k === 'string') {
            return (
              k.includes('contactCompany') ||
              k.includes('doorProxy') ||
              k.includes('company') ||
              (contactCompanyId && k.includes(`${contactCompanyId}`)) ||
              (doorProxyId && k.includes(`${doorProxyId}`)) ||
              (primaryId && k.includes(`${primaryId}`))
            )
          }
          return false
        })

        return shouldInvalidate
      },
    })

    // Also try explicit invalidation of the exact keys CompanyAttributesManager uses
    if (doorProxyId) {
      queryClient.invalidateQueries({ queryKey: ['doorProxy', doorProxyId] })
      // For doors, also invalidate the parent company if we have it
      if (contactCompanyId) {
        queryClient.invalidateQueries({
          queryKey: ['contactCompany', contactCompanyId],
        })
      }
    } else if (contactCompanyId) {
      queryClient.invalidateQueries({
        queryKey: ['contactCompany', contactCompanyId],
      })
    }

    // Also try the standard API query key pattern
    if (primaryId) {
      queryClient.invalidateQueries({ queryKey: ['company', primaryId] })
    }

    // IMPORTANT: Also invalidate chain data since CompanyAttributesManager prioritizes chainData
    // From the logs, we can see chainData is being used, so we need to invalidate chain queries too
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey
        if (!Array.isArray(key)) return false
        return key.some((k) => {
          if (typeof k === 'string') {
            return k.includes('chain') || k.includes('Chain')
          }
          // Also invalidate any queries with chain IDs if we can identify them
          return false
        })
      },
    })

    // Force refetch of chain data specifically (this will pick up the new tags)
    queryClient.refetchQueries({
      predicate: (query) => {
        const key = query.queryKey
        if (!Array.isArray(key)) return false
        return key[0] === 'chain'
      },
    })

    // Refresh relevant queries - using broader queryKey patterns to match actual table queries
    queryClient.refetchQueries({
      queryKey: ['deals-section'],
    })
    queryClient.refetchQueries({
      queryKey: ['contacts-table'],
    })

    // Refresh notes tables with more specific query keys based on NotesSection structure
    if (doorProxyId) {
      // For door pages, refresh the single notes table
      const notesTableKey = `notes-section-${contactCompanyId || doorProxyId}-NOTES_TABLE`
      queryClient.invalidateQueries({
        predicate: (query) => {
          return query.queryKey.includes(notesTableKey)
        },
      })
    } else if (contactCompanyId) {
      // For company pages, refresh both company and door notes tables
      const companyNotesTableKey = `company-notes-section-${contactCompanyId}-COMPANY_NOTES_TABLE`
      const doorNotesTableKey = `door-notes-section-${contactCompanyId}-DOOR_NOTES_TABLE`
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey
          return (
            key.includes(companyNotesTableKey) ||
            key.includes(doorNotesTableKey)
          )
        },
      })
    }
  }

  // Effect to trigger SuggestionModal button click when showSuggestEdit changes
  useEffect(() => {
    if (showSuggestEdit) {
      // Find and click the suggestion modal button
      setTimeout(() => {
        const suggestButtons = document.querySelectorAll('button')
        for (const button of suggestButtons) {
          const span = button.querySelector('span')
          if (span && span.textContent === 'Suggest an edit') {
            button.click()
            setShowSuggestEdit(false) // Reset the state
            break
          }
        }
      }, 100)
    }
  }, [showSuggestEdit])

  // No need for useEffect to trigger merge modal - we'll handle it directly

  return (
    <>
      <Dropdown
        className={`${className} focus:outline-none focus:ring-0`}
        buttonIcon={<PiToolbox size={20} />}
        buttonLabel="Actions"
        buttonVariant="primary"
        items={dropdownItems}
      />

      {/* Create Deal Modal */}
      {showCreateDeal && (
        <CreateDealModal
          show={showCreateDeal}
          handleClose={() => setShowCreateDeal(false)}
          targetCompanyId={contactCompanyId}
          targetDoorProxyId={doorProxyId}
          forDoorSelection={!!doorProxyId}
          tableTypeSlug={tableTypeSlug}
          companyTypeSlug={companyTypeSlug || 'restaurants-bars'}
          totalSelectedRows={1}
          optimisticUiTarget="deals-section"
          onDealCreated={handleRefreshData}
        />
      )}

      {/* Create Note Modal */}
      {showCreateNote && (
        <NotesModal
          show={showCreateNote}
          handleClose={() => setShowCreateNote(false)}
          chainId={doorProxyId ? undefined : contactCompanyId}
          contactCompanyId={doorProxyId ? undefined : contactCompanyId}
          doorProxyId={doorProxyId}
          onSubmitSuccess={handleRefreshData}
        />
      )}

      {/* Add Contact Modal */}
      {showAddContact && (
        <>
          <NewContactModal
            show={showAddContact}
            handleClose={() => setShowAddContact(false)}
            onContactCreated={handleRefreshData}
            forCompany={effectiveContactCompany}
            initialDoorIds={doorProxyId ? [doorProxyId] : undefined}
          />
        </>
      )}

      {/* Manage Tags Modal */}
      {showManageTags && (contactCompanyId || doorProxyId) && (
        <SingleItemTagAssignModal
          show={showManageTags}
          handleClose={() => setShowManageTags(false)}
          contactCompanyId={contactCompanyId}
          doorProxyId={doorProxyId}
          contactCompany={effectiveContactCompany}
          onSuccess={handleRefreshData}
          isDoorPage={!!doorProxyId}
        />
      )}

      {/* Add Distributor Modal */}
      {showAddDistributor && (contactCompanyId || doorProxyId) && (
        <SingleItemDistributorAssignModal
          show={showAddDistributor}
          handleClose={() => setShowAddDistributor(false)}
          contactCompanyId={contactCompanyId}
          doorProxyId={doorProxyId}
          contactCompany={effectiveContactCompany}
          onSuccess={handleRefreshData}
          isDoorPage={!!doorProxyId}
        />
      )}

      {/* Add GPO Modal */}
      {showAddGPO && (contactCompanyId || doorProxyId) && (
        <SingleItemGPOAssignModal
          show={showAddGPO}
          handleClose={() => setShowAddGPO(false)}
          contactCompanyId={contactCompanyId}
          doorProxyId={doorProxyId}
          contactCompany={effectiveContactCompany}
          onSuccess={handleRefreshData}
          isDoorPage={!!doorProxyId}
        />
      )}

      {/* Schedule Reminder Modal */}
      {featureFlag.enableReminders && showReminderModal && (
        <ReminderModal
          open={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          defaultCompanyId={doorProxyId ? undefined : contactCompanyId}
          defaultDoorProxyId={doorProxyId}
          onCreated={() => {
            setShowReminderModal(false)
            handleRefreshData()
          }}
        />
      )}

      {/* Merge Company Modal (for user-created companies) */}
      {isUserCreated && contactCompanyId && (
        <SingleChainMergeModal
          show={showMergeCompany}
          onClose={() => setShowMergeCompany(false)}
          contactCompanyId={contactCompanyId}
          contactCompanyName={companyName ?? ''}
        />
      )}

      {/* Hidden SuggestionModal that we'll trigger programmatically */}
      {!isUserCreated && (
        <div style={{ display: 'none' }}>
          <SuggestionModal
            contactCompanyId={contactCompanyId!}
            tableTypeSlug={tableTypeSlug}
            doorId={doorId}
            defaultValues={{
              suggested_name: companyName,
              suggested_website: websiteAddress ?? '',
              ...(doorId && {
                suggested_address: address?.addressInfo?.fullAddress,
                suggested_phone_number: phone,
              }),
            }}
          />
        </div>
      )}
    </>
  )
}
